import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, Divider, CircularProgress, Alert, Chip } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { NotificationService } from '../../../api/services/notificationService';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function InboxTab({ setNotification, isActive }) {
    const [inboxNotifications, setInboxNotifications] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(false);
    const [inboxError, setInboxError] = useState(null);

    const [inboxPage, setInboxPage] = useState(0);
    const [inboxRowsPerPage, setInboxRowsPerPage] = useState(20);
    const [totalInbox, setTotalInbox] = useState(0);

    useEffect(() => {
        if (isActive) {
            fetchInbox();
        }
    }, [isActive, inboxPage, inboxRowsPerPage]);

    const fetchInbox = async () => {
        setInboxLoading(true);
        setInboxError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || null;
            const params = {
                page: inboxPage,
                limit: inboxRowsPerPage
            };
            const data = await NotificationService.getInboxNotifications(tenantId, params);
            const notificationsArray = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
            setInboxNotifications(notificationsArray);
            setTotalInbox(extractTotalCount(data, notificationsArray));
        } catch (err) {
            console.error('Failed to fetch inbox notifications:', err);
            setInboxError('Failed to load your notifications.');
        } finally {
            setInboxLoading(false);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || null;
            await NotificationService.markAllRead(tenantId);
            setInboxNotifications(prev => prev.map(n => ({ ...n, status: 'Read' })));
            setNotification({ open: true, message: 'All notifications marked as read', severity: 'success' });
            window.dispatchEvent(new Event('notifications-read'));
        } catch (err) {
            console.error('Failed to mark all as read:', err);
            setNotification({ open: true, message: 'Failed to mark notifications as read', severity: 'error' });
        }
    };

    if (inboxLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Fetching inbox...</Typography>
            </Box>
        );
    }

    if (inboxError) {
        return <Alert severity="error">{inboxError}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button size="small" onClick={handleMarkAllRead} disabled={inboxNotifications.length === 0 || inboxNotifications.every(n => n.status === 'Read')}>
                    Mark All as Read
                </Button>
            </Box>
            {inboxNotifications.length > 0 ? (
                <List sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
                    {inboxNotifications.map((notif, index) => (
                        <React.Fragment key={notif.id || index}>
                            <ListItem alignItems="flex-start" sx={{ bgcolor: notif.status === 'Read' ? 'transparent' : 'rgba(25, 118, 210, 0.04)' }}>
                                <ListItemIcon>
                                    <NotificationsActiveIcon color={notif.status === 'Read' ? 'disabled' : 'primary'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body1" fontWeight={notif.status === 'Read' ? 'normal' : 'bold'}>
                                                {notif.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(notif.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <React.Fragment>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {notif.message}
                                            </Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <Chip size="small" label={notif.category || notif.type || 'Alert'} sx={{ height: 20, fontSize: '0.7rem' }} />
                                            </Box>
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                            {index < inboxNotifications.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            ) : (
                <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 3, border: '1px dashed #ddd' }}>
                    <NotificationsActiveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">Your inbox is empty</Typography>
                    <Typography variant="body2" color="text.disabled">You have no new notifications.</Typography>
                </Box>
            )}
            
            {inboxNotifications.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                        disabled={inboxPage === 0} 
                        onClick={() => setInboxPage(prev => prev - 1)}
                        sx={{ mr: 1 }}
                    >
                        Previous
                    </Button>
                    <Button 
                        disabled={inboxNotifications.length < inboxRowsPerPage} 
                        onClick={() => setInboxPage(prev => prev + 1)}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Box>
    );
}
